// @alamo/ui · review/ — L2.4 generic reason dialog (RHF + Zod).
//
// Domain-agnostic confirmation dialog: a required/optional reason (min-chars)
// plus an optional category picker (rendered via `Combobox` — never a raw
// native dropdown; reason uses the `Textarea` primitive). Domain-specific
// dialogs (approve, first-access) live in the caller and are injected into
// DecisionPanel via render-slots.

import { useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button, type ButtonProps } from '../Button/Button';
import { Combobox } from '../Combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../Dialog/Dialog';
import { Textarea } from '../Textarea/Textarea';

export interface ReasonDialogCategory {
  value: string;
  label: string;
}

export interface ReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  reasonLabel: string;
  reasonPlaceholder?: string;
  /** Minimum reason length. `0` = optional reason. */
  minChars?: number;
  /** Maximum reason length. */
  maxChars?: number;
  /** Optional category picker (Combobox). */
  categories?: ReasonDialogCategory[];
  categoryLabel?: string;
  confirmLabel: string;
  confirmVariant?: ButtonProps['variant'];
  isSubmitting?: boolean;
  onSubmit: (data: { reason: string; category?: string }) => void;
}

interface ReasonFormValues {
  reason: string;
  category?: string;
}

export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  reasonLabel,
  reasonPlaceholder,
  minChars = 0,
  maxChars = 500,
  categories,
  categoryLabel,
  confirmLabel,
  confirmVariant = 'primary',
  isSubmitting = false,
  onSubmit,
}: ReasonDialogProps) {
  const { t } = useTranslation('ui');
  const hasCategories = Boolean(categories && categories.length > 0);

  const schema = useMemo(
    () =>
      z
        .object({ reason: z.string().trim(), category: z.string().optional() })
        .superRefine((val, ctx) => {
          if (minChars > 0 && val.reason.length < minChars) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['reason'],
              message: t('review.reason_dialog.min_chars', { count: minChars }),
            });
          }
          if (val.reason.length > maxChars) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['reason'],
              message: t('review.reason_dialog.max_chars', { count: maxChars }),
            });
          }
          if (hasCategories && !val.category) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['category'],
              message: t('review.reason_dialog.category_required'),
            });
          }
        }),
    [minChars, maxChars, hasCategories, t],
  );

  const { register, handleSubmit, control, reset, formState } = useForm<ReasonFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '', category: categories?.[0]?.value },
  });

  useEffect(() => {
    if (open) reset({ reason: '', category: categories?.[0]?.value });
  }, [open, reset, categories]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <form
          onSubmit={handleSubmit((data) =>
            onSubmit({ reason: data.reason.trim(), category: data.category }),
          )}
        >
          <div className="space-y-4">
            {hasCategories && (
              <Controller
                control={control}
                name="category"
                render={({ field }) => (
                  <Combobox
                    label={categoryLabel}
                    options={(categories ?? []).map((c) => ({ value: c.value, label: c.label }))}
                    value={field.value ?? null}
                    onChange={(v) => field.onChange(v ?? undefined)}
                    error={formState.errors.category?.message}
                  />
                )}
              />
            )}
            <Textarea
              label={reasonLabel}
              required={minChars > 0}
              placeholder={reasonPlaceholder}
              error={formState.errors.reason?.message}
              rows={3}
              {...register('reason')}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('review.reason_dialog.cancel')}
            </Button>
            <Button type="submit" variant={confirmVariant} isLoading={isSubmitting}>
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
